/**
 * 클라이언트 측 PDF 다운샘플링 압축
 *
 * 큰 컬러 스캔 PDF (예: 30페이지 60MB) 를 기본 250DPI/JPEG 92% 로 다운샘플링하여
 * 30MB 이하로 압축한다. Cloud Run HTTP/1.x 32MiB 본문 한도 안에 들어가게 하는 것이 목적.
 *
 * 동작:
 *   1. pdfjs-dist 로 각 페이지를 canvas 에 렌더링 (target DPI)
 *   2. canvas 를 JPEG 로 인코딩 (지정 품질)
 *   3. pdf-lib 로 새 PDF 에 JPEG 페이지를 순서대로 임베드
 *   4. 단일 File 로 반환
 *
 * Trade-off:
 *   - 화질: 기본 250DPI/JPEG 92% 는 작은 글씨·연필 답안의 AI OCR 품질을 우선한다.
 *           그래도 30MB를 넘는 경우에만 200DPI/JPEG85 안전 fallback을 사용한다.
 *   - 속도: 페이지당 약 200~500ms (브라우저/디바이스에 따라)
 *   - 텍스트 selectable: 스캔 PDF 는 어차피 이미지 PDF 라 영향 없음
 *
 * 메모리 관리:
 *   - canvas 한 개를 페이지마다 재사용 (메모리 누수 방지)
 *   - 렌더 직후 width/height = 0 으로 GPU 메모리 즉시 해제
 *
 * @module pdfCompressor
 */

import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

// pdfjs-dist 5.x worker 설정 (webpack 5 + CRA 환경)
// import.meta.url 패턴으로 webpack 이 자동으로 worker 파일을 빌드 산출물에 포함시킴.
// SSR/Node 환경에서는 호출되지 않으므로 안전.
if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();
  } catch (e) {
    // import.meta.url 미지원 환경 → CDN fallback
    // pdfjs-dist 5.6.205 와 동일 버전 명시
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.6.205/pdf.worker.min.mjs';
  }
}

/**
 * 스캔 PDF 를 다운샘플링 압축한다.
 *
 * @param {File} pdfFile - 압축할 PDF File 객체
 * @param {Object} [options]
 * @param {number} [options.targetDpi=250] - 목표 DPI (낮을수록 작아짐)
 * @param {number} [options.jpegQuality=0.92] - JPEG 품질 (0.0 ~ 1.0)
 * @param {(currentPage:number, totalPages:number)=>void} [options.onProgress]
 * @returns {Promise<{ file: File, pageCount: number }>} 압축된 PDF File + 페이지 수.
 *   압축 후 더 커지면 원본 file 반환 (pageCount 는 동일).
 */
export async function compressScanPdf(pdfFile, options = {}) {
  const {
    targetDpi = 250,
    jpegQuality = 0.92,
    onProgress,
  } = options || {};

  if (!pdfFile || !(pdfFile instanceof Blob)) {
    throw new Error('compressScanPdf: 유효한 File/Blob 이 필요합니다');
  }

  const originalSize = pdfFile.size;
  const arrayBuffer = await pdfFile.arrayBuffer();

  // pdfjs 로 PDF 파싱
  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer,
    // 스캔 PDF는 보통 폰트 임베드 없음. CMap/font fetch 시도 비활성화로 속도 향상.
    cMapUrl: undefined,
    standardFontDataUrl: undefined,
    isEvalSupported: false,
    disableFontFace: true,
  });
  const pdfDoc = await loadingTask.promise;
  const pageCount = pdfDoc.numPages;

  if (pageCount < 1) {
    throw new Error('PDF 에 페이지가 없습니다');
  }

  // pdf-lib 로 새 PDF 빌드
  const newPdf = await PDFDocument.create();
  newPdf.setTitle(pdfFile.name);

  // canvas 재사용 (메모리 누수 방지)
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: false });

  // pdfjs 의 기본 viewport 단위는 72DPI 기준 → scale = targetDpi / 72
  const renderScale = targetDpi / 72;

  try {
    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex++) {
      try { onProgress?.(pageIndex, pageCount); } catch (_) { /* ignore */ }

      const page = await pdfDoc.getPage(pageIndex);
      const viewport = page.getViewport({ scale: renderScale });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);

      // 흰 배경으로 클리어 (투명 PDF 페이지 대비)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({
        canvasContext: ctx,
        viewport,
        intent: 'print',
      }).promise;

      // page 객체 cleanup (pdfjs 메모리 누수 방지)
      try { page.cleanup(); } catch (_) { /* ignore */ }

      // canvas → JPEG Blob → ArrayBuffer
      const jpegBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error(`페이지 ${pageIndex} JPEG 변환 실패`));
          },
          'image/jpeg',
          jpegQuality,
        );
      });
      const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());

      // pdf-lib 에 JPEG 임베드
      const jpegImage = await newPdf.embedJpg(jpegBytes);
      const newPage = newPdf.addPage([jpegImage.width, jpegImage.height]);
      newPage.drawImage(jpegImage, {
        x: 0,
        y: 0,
        width: jpegImage.width,
        height: jpegImage.height,
      });
    }
  } finally {
    // canvas 메모리 즉시 해제
    canvas.width = 0;
    canvas.height = 0;
    // pdfjs 문서 cleanup
    try { await pdfDoc.cleanup(); } catch (_) { /* ignore */ }
    try { await pdfDoc.destroy(); } catch (_) { /* ignore */ }
  }

  // 새 PDF 저장
  // useObjectStreams: false 가 핵심 - 객체를 압축 stream 에 넣지 않아서
  // 1) 다른 페이지 수 감지 코드(detectScanPdf 의 raw text /Type /Page 정규식)와 호환
  // 2) 외부 PDF 도구(파이썬 PyPDF2 등)와 호환성 안전
  // 단점: 파일 크기 약 1~3% 증가. 30MB 한도에 비해 무시 가능.
  const newPdfBytes = await newPdf.save({
    useObjectStreams: false,
    addDefaultPage: false,
  });

  // 압축 결과가 원본보다 크면 (이미 잘 압축된 PDF) 원본 그대로 반환
  // (단, 원본이 30MB 초과면 압축 결과가 더 커도 압축본 반환 — 어차피 원본은 못 올림)
  const HARD_LIMIT = 30 * 1024 * 1024;
  if (newPdfBytes.byteLength >= originalSize && originalSize <= HARD_LIMIT) {
    return { file: pdfFile, pageCount };
  }

  const compressedBlob = new Blob([newPdfBytes], { type: 'application/pdf' });
  const compressedName = pdfFile.name.replace(/\.pdf$/i, '_compressed.pdf');
  const compressedFile = new File([compressedBlob], compressedName, { type: 'application/pdf' });
  return {
    file: compressedFile,
    pageCount,
    compressionProfile: {
      targetDpi,
      jpegQuality,
    },
  };
}

/**
 * PDF 페이지 수를 pdfjs-dist로 안정적으로 계산한다.
 *
 * raw text 정규식(`/Type /Page`)은 이미지 PDF나 object stream PDF에서 실패할 수 있어
 * 스캔 채점 모드 진입이 누락될 수 있다. 스캔 채점 업로드 판정에는 이 함수를 우선 사용한다.
 *
 * @param {File|Blob} pdfFile
 * @returns {Promise<number>}
 */
export async function getPdfPageCount(pdfFile) {
  if (!pdfFile || !(pdfFile instanceof Blob)) {
    return 0;
  }
  const arrayBuffer = await pdfFile.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer,
    cMapUrl: undefined,
    standardFontDataUrl: undefined,
    isEvalSupported: false,
    disableFontFace: true,
  });
  const pdfDoc = await loadingTask.promise;
  try {
    return pdfDoc.numPages || 0;
  } finally {
    try { await pdfDoc.cleanup(); } catch (_) { /* ignore */ }
    try { await pdfDoc.destroy(); } catch (_) { /* ignore */ }
  }
}

/**
 * PDF 가 압축이 필요한지 판정 + 압축 시도.
 *
 * 단일 30MB 한도를 넘는 PDF 만 자동 압축한다. 안전 마진을 위해 "가까운 한도" 도 압축 대상.
 *
 * @param {File} pdfFile
 * @param {Object} [options]
 * @param {number} [options.thresholdBytes] - 이 크기를 넘으면 압축 진행. 기본: 28MB
 * @param {(currentPage:number, totalPages:number)=>void} [options.onProgress]
 * @returns {Promise<{ compressed: boolean, file: File, originalSize: number, compressedSize: number, pageCount: number|null }>}
 *   pageCount: 압축이 실제로 진행된 경우만 채워짐 (호출자가 detectScanPdf 우회 가능).
 */
export async function maybeCompressScanPdf(pdfFile, options = {}) {
  const {
    thresholdBytes = 28 * 1024 * 1024, // 30MB 한도에 2MB 안전 마진
    onProgress,
  } = options || {};

  const originalSize = pdfFile.size;

  if (originalSize <= thresholdBytes) {
    return {
      compressed: false,
      file: pdfFile,
      originalSize,
      compressedSize: originalSize,
      pageCount: null,
    };
  }

  let { file: compressedFile, pageCount, compressionProfile } = await compressScanPdf(pdfFile, {
    targetDpi: 250,
    jpegQuality: 0.92,
    onProgress,
  });

  const HARD_LIMIT = 30 * 1024 * 1024;
  if (compressedFile.size > HARD_LIMIT) {
    ({ file: compressedFile, pageCount, compressionProfile } = await compressScanPdf(pdfFile, {
      targetDpi: 200,
      jpegQuality: 0.85,
      onProgress,
    }));
  }

  return {
    compressed: compressedFile !== pdfFile,
    file: compressedFile,
    originalSize,
    compressedSize: compressedFile.size,
    pageCount,
    compressionProfile,
  };
}
